import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";

const STORAGE_KEY = "datasyte-cookie-prefs";

type Prefs = { analytics: boolean; functional: boolean };

const readPrefs = (): Prefs => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { analytics: false, functional: true, ...(JSON.parse(raw) as Partial<Prefs>) };
  } catch {
    /* ignore */
  }
  return { analytics: false, functional: true };
};

export const CookiePreferences = ({ className }: { className?: string }) => {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState<Prefs>({ analytics: false, functional: true });

  useEffect(() => {
    if (open) setPrefs(readPrefs());
  }, [open]);

  const save = () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* ignore */
    }
    setOpen(false);
    toast.success("Cookie preferences saved");
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className={className}>
          Cookie Preferences
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Cookie preferences</DialogTitle>
          <DialogDescription>
            Choose what this site may store on your device. Strictly necessary storage cannot be
            switched off because the site will not function without it.
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-4">
          <li className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-semibold text-foreground">Strictly necessary</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Security, routing, and your saved cookie choice. Always on.
              </p>
            </div>
            <Switch checked disabled aria-label="Strictly necessary cookies, always on" />
          </li>
          <li className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-semibold text-foreground">Functional</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Remembers interface preferences such as dismissed notices.
              </p>
            </div>
            <Switch
              checked={prefs.functional}
              onCheckedChange={(v) => setPrefs((p) => ({ ...p, functional: v }))}
              aria-label="Functional cookies"
            />
          </li>
          <li className="flex items-start justify-between gap-4 rounded-lg border border-border p-4">
            <div>
              <p className="text-sm font-semibold text-foreground">Analytics</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Aggregate, non-identifying measurement of page and button usage.
              </p>
            </div>
            <Switch
              checked={prefs.analytics}
              onCheckedChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
              aria-label="Analytics cookies"
            />
          </li>
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={() => setPrefs({ analytics: false, functional: false })}>
            Reject optional
          </Button>
          <Button onClick={save}>Save preferences</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
